// Right-hand Inspector column, opened from the contextual toolbar. Tabs are
// contextual per selection type: text gets [Style, Position] (its Styles
// presets live inside the Style tab); everything else keeps
// [Effects, Position, Styles]. Closes on Esc, ×, or when the selection
// clears (parent handles the last one).
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EffectsTab from './EffectsTab';
import PositionTab from './PositionTab';
import StylesTab from './StylesTab';
import TextStyleTab from './text/TextStyleTab';
import { isTextType } from '../hooks/useSelectionProps';

const TABS = [
  { id: 'effects', labelKey: 'inspector.tabs.effects' },
  { id: 'position', labelKey: 'inspector.tabs.position' },
  { id: 'styles', labelKey: 'inspector.tabs.styles' },
];

const TEXT_TABS = [
  { id: 'effects', label: 'Style' },
  { id: 'position', labelKey: 'inspector.tabs.position' },
];

export default function InspectorPanel({
  editor, selectedObjects, tab, onTabChange, onClose, sectionRequest,
}) {
  const { t: tr } = useTranslation('editor');
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Inputs, pickers and fabric's in-canvas text editing own Esc (§6.5)
      const t = e.target;
      if (
        t instanceof Element
        && (t.matches('input, textarea, select, [contenteditable]') || t.isContentEditable)
      ) {
        return;
      }
      if (editor?.canvas?.getActiveObject()?.isEditing) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, editor]);

  if (!tab) return null;

  const isText = selectedObjects.length > 0 && isTextType(selectedObjects[0]?.type);
  const tabs = isText ? TEXT_TABS : TABS;
  // A stale 'styles' tab id can arrive from a previous non-text selection
  const activeTab = isText && tab === 'styles' ? 'effects' : tab;

  return (
    <aside className="cce-inspector">
      <div className="cce-inspector-head">
        <div className="cce-inspector-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`cce-inspector-tab${activeTab === t.id ? ' active' : ''}`}
              onClick={() => onTabChange(t.id)}
            >
              {t.labelKey ? tr(t.labelKey) : t.label}
            </button>
          ))}
        </div>
        <button type="button" className="cce-close-btn" aria-label={tr('inspector.closePanel')} onClick={onClose}>
          ×
        </button>
      </div>

      <div className="cce-inspector-body">
        {selectedObjects.length === 0 ? (
          <div className="cce-inspector-empty">{tr('inspector.emptySelect')}</div>
        ) : (
          <>
            {activeTab === 'effects' && (isText
              // sectionRequest (R1 ribbon drawer links): which accordion to
              // unfold — meaningful only here, the one accordion surface.
              ? (
                <TextStyleTab
                  editor={editor}
                  selectedObjects={selectedObjects}
                  sectionRequest={sectionRequest}
                />
              )
              : <EffectsTab editor={editor} selectedObjects={selectedObjects} />)}
            {activeTab === 'position' && <PositionTab editor={editor} selectedObjects={selectedObjects} />}
            {activeTab === 'styles' && !isText && <StylesTab editor={editor} selectedObjects={selectedObjects} />}
          </>
        )}
      </div>
    </aside>
  );
}
